import { BusinessSummary } from "../../model/business/BusinessSummary";
import i18n from "../../i18n";
import {BusinessSettings} from "../../model/business/BusinessSettings";
import {BusinessDetailsInput} from "./BusinessDetailsInput";
import {BusinessDetails} from "../../model/business/BusinessDetails";
import {BusinessSettingsInput} from "./BusinessSettingsInput";
import {BusinessNotificationSettingsInput} from "./BusinessNotificationSettingsInput";
import {BusinessHours} from "../../model/business/BusinessHours";
import {BusinessHoursInput} from "./BusinessHoursInput";
import {BusinessCreateInput} from "./BusinessCreateInput";

// const API_URL = "http://localhost:8082/graphql";
const API_URL= process.env.REACT_APP_BUSINESS_SERVICE_BASE_URL || '';

/** Result of requesting CNPJ ownership verification. */
export interface CnpjOtpRequestResult {
    /** SENT | AUTO_VERIFIED | NO_CONTACT */
    status: string;
    /** Masked Receita email the OTP went to (e.g. j****@gmail.com) — set when SENT. */
    maskedEmail?: string | null;
}

/** Result of submitting an OTP code. */
export interface CnpjVerifyResult {
    verified: boolean;
    method?: string | null;
}

/**
 * Request CNPJ ownership verification. The backend re-fetches the CNPJ to get
 * the Receita-registered email (the client cannot pick the destination) and
 * either emails a code, auto-verifies (login email matches), or reports no contact.
 */
export async function requestCnpjOtp(cnpj: string, token: string): Promise<CnpjOtpRequestResult> {
    const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation requestCnpjOtp($cnpj: String!) { requestCnpjOtp(cnpj: $cnpj) { status maskedEmail } }`,
            variables: { cnpj },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(", "));
    return json.data.requestCnpjOtp as CnpjOtpRequestResult;
}

/** Submit an OTP code for a CNPJ. verified=false on wrong/expired code. */
export async function verifyCnpjOtp(cnpj: string, code: string, token: string): Promise<CnpjVerifyResult> {
    const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `mutation verifyCnpjOtp($cnpj: String!, $code: String!) { verifyCnpjOtp(cnpj: $cnpj, code: $code) { verified method } }`,
            variables: { cnpj, code },
        }),
    });
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e: { message: string }) => e.message).join(", "));
    return json.data.verifyCnpjOtp as CnpjVerifyResult;
}

/**
 * Create a new business for the logged-in user (owner is derived from the JWT
 * on the backend — no client-supplied id). Returns the new BusinessSummary.
 * Used by the onboarding wizard.
 */
export async function createBusiness(
    input: BusinessCreateInput,
    token: string
): Promise<BusinessSummary> {
    const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
        body: JSON.stringify({
            query: `
                mutation createBusiness($input: BusinessCreateInput!) {
                    createBusiness(input: $input) {
                        id name slug active image
                        brandColor brandColorStrong brandColorSoft
                        acceptedPaymentMethods currency timezone language
                    }
                }
            `,
            variables: { input },
        }),
    });
    const json = await res.json();
    if (json.errors) {
        throw new Error(json.errors.map((e: { message: string }) => e.message).join(", "));
    }
    return json.data.createBusiness as BusinessSummary;
}


export async function findAllBusinessSummaryByOwnerId(token: string): Promise<BusinessSummary[]> {
    if (!token) {
        console.error("No token available");
        return [];
    }

    try {
        const businessResponse = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
            },
            body: JSON.stringify({
                // Identity is derived from the JWT on the backend — we send no
                // ownerId/email, so the server can only return the caller's own
                // businesses (owned OR member of). Secure by construction.
                query: `
                    query findMyBusinesses {
                        findMyBusinesses {
                            id,
                            name,
                            active,
                            image,
                            brandColor,
                            brandColorStrong,
                            brandColorSoft,
                            acceptedPaymentMethods,
                            currency,
                            timezone,
                            language,
                            timeFormat,
                            slug,
                            country,
                            pixKey,
                            bankTransferInfo
                        }
                    }
                `,
            }),
        });

        const businessResult = await businessResponse.json();

        if (businessResult.errors) {
            console.error("GraphQL Errors:", businessResult.errors);
            return [];
        } else if (businessResult?.data?.findMyBusinesses) {
            return businessResult.data.findMyBusinesses as BusinessSummary[];
        } else {
            console.error("No data found");
            return [];
        }
    } catch (error) {
        console.error("Error fetching business data:", error);
        return [];
    }
}

export async function findBusinessDetailsByBusinessId(businessId: number | undefined,
                                                      token: string): Promise<BusinessDetails> {
    if (!token) {
        console.error("No token available");
        throw new Error(i18n.t('service.errors.noToken'));
    }

    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
            },
            body: JSON.stringify({
                variables: { businessId: String(businessId) },
                query: `
                    query findBusinessDetailsByBusinessId($businessId: ID!) {
                        findBusinessDetailsByBusinessId(businessId: $businessId) {
                            id
                            name
                            ownerId
                            active
                            language
                            timeFormat
                            timezone
                            currency
                            image
                            brandColor
                            brandColorStrong
                            brandColorSoft
                            acceptedPaymentMethods
                            pixKey
                            bankTransferInfo
                            website
                            address1
                            address2
                            city
                            state
                            country
                            zipCode
                            retailsSalesTaxRate
                            discountAllowedRateDefault
                            discountAllowedRate1
                            discountAllowedRate2
                            discountAllowedRate3
                            discountAllowedRate4
                            lastLoginOn
                            coverImage
                            category
                            headline
                            about
                            yearsExperience
                            highlight
                            publicPhone
                            instagram
                            facebook
                            onboardingComplete
                            cnpj
                            legalName
                            cnae
                            registeredEmail
                            registeredPhone
                        }
                    }
                `,
            }),
        });

        const result = await response.json();

        if (result.errors) {
            // console.error("GraphQL Errors:", result.errors);
            throw new Error(i18n.t('service.errors.fetchDetails', { error: result.errors.map((e: { message: any; }) => e.message).join(", ") }));
        } else if (result?.data?.findBusinessDetailsByBusinessId) {
            return result.data.findBusinessDetailsByBusinessId as BusinessDetails;
        } else {
            // console.error("No data found");
            throw new Error(i18n.t('service.errors.noData', { id: businessId }));
        }
    } catch (error) {
        // console.error("Error fetching business data:", error);
        if (error instanceof Error) {
            throw new Error(i18n.t('service.errors.fetchData', { error: error.message }));
        } else {
            throw new Error(i18n.t('service.errors.fetchDataGeneric'));
        }
    }
}

export async function saveBusinessDetails(businessDetails: BusinessDetailsInput, token: string) {

    if (!token) {
        console.error("No token available");
        return { success: false, error: "Authentication token is missing" };
    }

    const mutation = `
        mutation saveDetails($input: BusinessDetailsInput!) {
            saveDetails(input: $input) {
                id
                name
                language
                timeFormat
                timezone
                currency
                ownerId
                active
                image
                brandColor
                brandColorStrong
                brandColorSoft
                acceptedPaymentMethods
                website
                address1
                address2
                city
                state
                country
                zipCode
                retailsSalesTaxRate
                discountAllowedRateDefault
                discountAllowedRate1
                discountAllowedRate2
                discountAllowedRate3
                discountAllowedRate4
                lastLoginOn
                coverImage
                category
                headline
                about
                yearsExperience
                highlight
                publicPhone
                instagram
                facebook
                onboardingComplete
            }
        }
    `;

    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
            },
            body: JSON.stringify({
                query: mutation,
                variables: {
                    input: businessDetails
                },
            }),
        });

        const result = await response.json();
        if (result.errors) {
            console.error('GraphQL Errors:', result.errors);
            // Flatten the GraphQL errors array into a readable string so the UI
            // shows the real message instead of "[object Object]".
            const message = Array.isArray(result.errors)
                ? result.errors.map((e: { message?: string }) => e?.message ?? String(e)).join('; ')
                : String(result.errors);
            return { success: false, error: message };
        } else {
            console.log('Business Details saved successfully:', result.data.saveDetails);
            return { success: true, data: result.data.saveDetails };
        }
    } catch (error) {
        console.error("Error saving save Details data:", error);
        const message = error instanceof Error ? error.message : String(error);
        return { success: false, error: message };
    }
}

/** Update the discount-allowed rates (Settings > Payment tab). Gated by PAYMENT_SETTINGS. */
export async function saveDiscountRates(
    businessId: number | string,
    rates: {
        discountAllowedRateDefault?: number | null;
        discountAllowedRate1?: number | null;
        discountAllowedRate2?: number | null;
        discountAllowedRate3?: number | null;
        discountAllowedRate4?: number | null;
    },
    token: string
): Promise<{ success: boolean; error?: string }> {
    if (!token) return { success: false, error: "Authentication token is missing" };
    const mutation = `
        mutation saveDiscountRates($businessId: ID!, $d: Float, $r1: Float, $r2: Float, $r3: Float, $r4: Float) {
            saveDiscountRates(businessId: $businessId, discountAllowedRateDefault: $d,
                discountAllowedRate1: $r1, discountAllowedRate2: $r2, discountAllowedRate3: $r3, discountAllowedRate4: $r4)
        }
    `;
    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
            body: JSON.stringify({
                query: mutation,
                variables: {
                    businessId,
                    d: rates.discountAllowedRateDefault ?? null,
                    r1: rates.discountAllowedRate1 ?? null,
                    r2: rates.discountAllowedRate2 ?? null,
                    r3: rates.discountAllowedRate3 ?? null,
                    r4: rates.discountAllowedRate4 ?? null,
                },
            }),
        });
        const result = await response.json();
        if (result.errors) {
            const message = Array.isArray(result.errors)
                ? result.errors.map((e: { message?: string }) => e?.message ?? String(e)).join('; ')
                : String(result.errors);
            return { success: false, error: message };
        }
        return { success: true };
    } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
}

/** Update ONLY the business's accepted payment methods (Settings > Payment tab).
 *  Backend gates this by the PAYMENT_SETTINGS permission. */
export async function saveAcceptedPaymentMethods(
    businessId: number | string,
    acceptedPaymentMethods: string,
    token: string,
    pixKey?: string | null,
    bankTransferInfo?: string | null
): Promise<{ success: boolean; error?: string }> {
    if (!token) return { success: false, error: "Authentication token is missing" };
    const mutation = `
        mutation saveAcceptedPaymentMethods($businessId: ID!, $methods: String, $pixKey: String, $bankTransferInfo: String) {
            saveAcceptedPaymentMethods(businessId: $businessId, acceptedPaymentMethods: $methods, pixKey: $pixKey, bankTransferInfo: $bankTransferInfo)
        }
    `;
    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt" },
            body: JSON.stringify({
                query: mutation,
                variables: { businessId, methods: acceptedPaymentMethods, pixKey: pixKey ?? null, bankTransferInfo: bankTransferInfo ?? null },
            }),
        });
        const result = await response.json();
        if (result.errors) {
            const message = Array.isArray(result.errors)
                ? result.errors.map((e: { message?: string }) => e?.message ?? String(e)).join('; ')
                : String(result.errors);
            return { success: false, error: message };
        }
        return { success: true };
    } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
}

export async function findBusinessSettingByBusinessId(businessId: number | undefined, token: string): Promise<BusinessSettings> {
    if (!token) {
        console.error("No token available");
        throw new Error(i18n.t('service.errors.noToken'));
    }

    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
            },
            body: JSON.stringify({
                variables: { businessId: String(businessId) },
                query: `
                    query findBusinessSettingByBusinessId($businessId: ID!) {
                        findBusinessSettingByBusinessId(businessId: $businessId) {
                          id
                          businessId
                          acceptBookingAtFixedLocation
                          acceptBookingOutsideLocation
                          openOnHolidays
                          onlineBookingType
                          autoApproveAppointment
                          cardOnBookingType
                          preAuthorizationPercentage
                          bookingWindow
                          bookingIncrement
                          allowAdvancedBooking
                          advancedBookingWindow
                          allowBookingOutsideBusinessHours
                          allowWaitlist
                          waitlistWindowDays
                          notifyWaitlistAvailableSpot
                          allowOnlineCancellation
                          requireCancellationPolicy
                          cancellationPolicy
                          cancellationWindow
                          cancellationFee
                          waitlistAvailableSpotConfirmationWindow
                          enableWaitingRoom
                healthMode
                          healthMode
                          askClientForReview
                          sendNewBookingEmail
                          sendNewBookingSMS
                          sendRescheduleBookingEmail
                          sendRescheduleBookingSMS
                          sendCancellationBookingEmail
                          sendCancellationBookingSMS
                          sendDeclineBookingEmail
                          sendDeclineBookingSMS
                          sendNewEntryWaitListEmail
                          sendNewEntryWaitListSMS
                          sendCancellationWaitListEmail
                          sendCancellationWaitListSMS
                          sendConfirmationEmail
                          textForConfirmationEmail
                          sendConfirmationSMS
                          textForConfirmationSMS
                          sendAppointmentReminderEmail
                          textForAppointmentReminderEmail
                          sendAppointmentReminderSMS
                          textForAppointmentReminderSMS
                          sendFinishAppointmentEmail
                          textForFinishAppointmentEmail
                          sendFinishAppointmentSMS
                          textForFinishAppointmentSMS
                          sendAppointmentReebokReminderEmail
                          textForAppointmentRebookReminderEmail
                          sendBirthdayEmail
                          textForBirthdayEmail
                          sendBirthdaySMS
                          textForBirthdaySMS
                          textForNewBookingEmail
                          textForNewBookingSMS
                          textForRescheduleBookingEmail
                          textForRescheduleBookingSMS
                          textForCancellationBookingEmail
                          textForCancellationBookingSMS
                          textForDeclineBookingEmail
                          textForDeclineBookingSMS
                          textForNewEntryWaitListEmail
                          textForNewEntryWaitListSMS
                          textForCancellationWaitListEmail
                          textForCancellationWaitListSMS
                          textForReviewEmail
                          sendNewBookingInApp
                          sendNewBookingPush
                          sendRescheduleBookingInApp
                          sendRescheduleBookingPush
                          sendCancellationBookingInApp
                          sendCancellationBookingPush
                          sendDeclineBookingInApp
                          sendDeclineBookingPush
                          sendNewEntryWaitListInApp
                          sendNewEntryWaitListPush
                          sendCancellationWaitListInApp
                          sendCancellationWaitListPush
                          sendConfirmationInApp
                          sendConfirmationPush
                          sendAppointmentReminderInApp
                          sendAppointmentReminderPush
                          sendFinishAppointmentInApp
                          sendFinishAppointmentPush
                          sendAppointmentReebokReminderInApp
                          sendAppointmentReebokReminderPush
                          sendBirthdayInApp
                          sendBirthdayPush
                          notifyTeamNewBooking
                          notifyTeamCancellation
                          notifyTeamReschedule
                          notifyTeamIncludeOwner
                        }
                    }
                `,
            }),
        });

        const result = await response.json();

        if (result.errors) {
            // console.error("GraphQL Errors:", result.errors);
            throw new Error(i18n.t('service.errors.fetchSettings', { error: result.errors.map((e: { message: any; }) => e.message).join(", ") }));
        } else if (result?.data?.findBusinessSettingByBusinessId) {
            return result.data.findBusinessSettingByBusinessId as BusinessSettings;
        } else {
            // console.error("No data found");
            throw new Error(i18n.t('service.errors.noData', { id: businessId }));
        }
    } catch (error) {
        // console.error("Error fetching business data:", error);
        if (error instanceof Error) {
            throw new Error(i18n.t('service.errors.fetchData', { error: error.message }));
        } else {
            throw new Error(i18n.t('service.errors.fetchDataGeneric'));
        }
    }
}


export async function findBusinessHoursByBusinessId(businessId: number | undefined, token: string): Promise<BusinessHours[]> {
    if (!token) {
        console.error("No token available");
        throw new Error(i18n.t('service.errors.noToken'));
    }

    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
            },
            body: JSON.stringify({
                variables: { businessId: String(businessId) },
                query: `
                    query findBusinessHoursByBusinessId($businessId: ID!) {
                        findBusinessHoursByBusinessId(businessId: $businessId) {
                            id
                            dayOfWeek
                            openTime
                            closeTime
                            breakStart
                            breakEnd
                            overnightEnd
                            businessId
                        }
                    }
                `,
            }),
        });

        const result = await response.json();

        if (result.errors) {
            // console.error("GraphQL Errors:", result.errors);
            throw new Error(i18n.t('service.errors.fetchHours', { error: result.errors.map((e: { message: any; }) => e.message).join(", ") }));
        } else if (result?.data?.findBusinessHoursByBusinessId) {
            return result.data.findBusinessHoursByBusinessId as BusinessHours[];
        } else {
            // console.error("No data found");
            throw new Error(i18n.t('service.errors.noData', { id: businessId }));
        }
    } catch (error) {
        // console.error("Error fetching business data:", error);
        if (error instanceof Error) {
            throw new Error(i18n.t('service.errors.fetchData', { error: error.message }));
        } else {
            throw new Error(i18n.t('service.errors.fetchDataGeneric'));
        }
    }
}

export async function saveBusinessSettings(businessSettingsInput: BusinessSettingsInput, token: string) {
    if (!token) {
        console.error("No token available");
        return { success: false, error: "Authentication token is missing" };
    }
    const mutation = `
        mutation saveSettings($input: BusinessSettingsInput!) {
            saveSettings(input: $input) {
                id
                openOnHolidays
                onlineBookingType
                autoApproveAppointment
                cardOnBookingType
                preAuthorizationPercentage
                bookingWindow
                bookingIncrement
                allowAdvancedBooking
                advancedBookingWindow
                allowBookingOutsideBusinessHours
                allowOnlineCancellation
                requireCancellationPolicy
                cancellationPolicy
                cancellationWindow
                cancellationFee
                allowWaitlist
                waitlistWindowDays
                notifyWaitlistAvailableSpot
                waitlistAvailableSpotConfirmationWindow
                enableWaitingRoom
                acceptBookingAtFixedLocation
                acceptBookingOutsideLocation
                askClientForReview
            }
        }
    `;

    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
            },
            body: JSON.stringify({
                query: mutation,
                variables: {
                    input: businessSettingsInput,
                },
            }),
        });

        const result = await response.json();
        if (result.errors) {
            throw new Error(i18n.t('service.errors.saveSettings', { error: result.errors.map((e: { message: any; }) => e.message).join(", ") }));
        } else {
            console.log('Business Settings saved successfully:', result.data.saveSettings);
            return { success: true, data: result.data.saveSettings };
        }
    } catch (error) {
        console.error("Error saving Business Settings data:", error);
        return { success: false, error: error };
    }
}


export async function saveBusinessNotificationSettings(businessNotificationSettingsInput: BusinessNotificationSettingsInput, token: string) {
    if (!token) {
        console.error("No token available");
        return { success: false, error: "Authentication token is missing" };
    }
    const mutation = `
        mutation saveNotificationSettings($input: BusinessNotificationSettingsInput!) {
            saveNotificationSettings(input: $input) {
                id
                askClientForReview
                sendNewBookingEmail
                sendNewBookingSMS
                sendRescheduleBookingEmail
                sendRescheduleBookingSMS
                sendCancellationBookingEmail
                sendCancellationBookingSMS
                sendDeclineBookingEmail
                sendDeclineBookingSMS
                sendNewEntryWaitListEmail
                sendNewEntryWaitListSMS
                sendCancellationWaitListEmail
                sendCancellationWaitListSMS
                sendConfirmationEmail
                textForConfirmationEmail
                sendConfirmationSMS
                textForConfirmationSMS
                sendAppointmentReminderEmail
                textForAppointmentReminderEmail
                sendAppointmentReminderSMS
                textForAppointmentReminderSMS
                sendFinishAppointmentEmail
                textForFinishAppointmentEmail
                sendFinishAppointmentSMS
                textForFinishAppointmentSMS
                sendAppointmentReebokReminderEmail
                textForAppointmentRebookReminderEmail
                sendBirthdayEmail
                textForBirthdayEmail
                sendBirthdaySMS
                textForBirthdaySMS
                textForNewBookingEmail
                textForNewBookingSMS
                textForRescheduleBookingEmail
                textForRescheduleBookingSMS
                textForCancellationBookingEmail
                textForCancellationBookingSMS
                textForDeclineBookingEmail
                textForDeclineBookingSMS
                textForNewEntryWaitListEmail
                textForNewEntryWaitListSMS
                textForCancellationWaitListEmail
                textForCancellationWaitListSMS
                textForReviewEmail
                sendNewBookingInApp
                sendNewBookingPush
                sendRescheduleBookingInApp
                sendRescheduleBookingPush
                sendCancellationBookingInApp
                sendCancellationBookingPush
                sendDeclineBookingInApp
                sendDeclineBookingPush
                sendNewEntryWaitListInApp
                sendNewEntryWaitListPush
                sendCancellationWaitListInApp
                sendCancellationWaitListPush
                sendConfirmationInApp
                sendConfirmationPush
                sendAppointmentReminderInApp
                sendAppointmentReminderPush
                sendFinishAppointmentInApp
                sendFinishAppointmentPush
                sendAppointmentReebokReminderInApp
                sendAppointmentReebokReminderPush
                sendBirthdayInApp
                sendBirthdayPush
            }
        }
    `;

    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
            },
            body: JSON.stringify({
                query: mutation,
                variables: {
                    input: businessNotificationSettingsInput,
                },
            }),
        });

        const result = await response.json();
        if (result.errors) {
            throw new Error(i18n.t('service.errors.saveNotification', { error: result.errors.map((e: { message: any; }) => e.message).join(", ") }));
        } else {
            console.log('Business Notification Settings saved successfully:', result.data.saveSettings);
            return { success: true, data: result.data.saveSettings };
        }
    } catch (error) {
        console.error("Error saving Business Notification Settings data:", error);
        return { success: false, error: error };
    }
}

export async function saveBusinessHours(input: BusinessHoursInput[], token: string) {
    if (!token) {
        throw new Error(i18n.t('service.errors.tokenMissing'));
    }

    const mutation = `
        mutation saveBusinessHours($input: [BusinessHoursInput!]!) {
            saveBusinessHours(input: $input) {
                id
                dayOfWeek
                openTime
                closeTime
                breakStart
                breakEnd
                overnightEnd
                businessId
            }
        }
    `;

    try {
        const response = await fetch(API_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
            },
            body: JSON.stringify({ query: mutation, variables: { input } }),
        });

        const result = await response.json();

        if (result.errors) {
            throw new Error(result.errors.map((e: { message: string }) => e.message).join(", "));
        }

        return { success: true };
    } catch (error) {
        console.error("Error saving business hours:", error);
        return { success: false, error: error };
    }
}

/** Remove one saved business-hours row — used when a day is turned OFF (the save
 *  payload only carries selected days, so a previously-saved day is cleared here). */
export async function deleteBusinessHours(id: string | number, token: string): Promise<boolean> {
    const res = await fetch(API_URL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({
            query: `mutation deleteBusinessHours($id: ID!) { deleteBusinessHours(id: $id) }`,
            variables: { id: String(id) },
        }),
    });
    const json = await res.json();
    if (json.errors) {
        throw new Error(json.errors.map((e: { message: string }) => e.message).join(", "));
    }
    return !!json.data?.deleteBusinessHours;
}